import React from 'react';
import { clsx } from 'clsx';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from '@/components/ui/SearchableSelect';
import {
  BILLING_MONTHS,
  BILLING_STATEMENT_TYPES,
  BILLING_STATUS_OPTIONS,
  billingYearOptions,
  hasBillingFilters,
  type BillingSearch,
} from '../billing-search';
import { BillingMonthNav } from './BillingMonthNav';

interface BillingFiltersProps {
  value: BillingSearch;
  onChange: (next: BillingSearch) => void;
  counterpartyOptions: SearchableSelectOption[];
  /**
   * Name of the currently filtered counterparty, resolved by the page. The
   * options are one searched window of a ~16k registry, so the selected one is
   * usually not among them (LR-572).
   */
  counterpartyName?: string;
  /** Debounced query from the counterparty select — the page fetches with it. */
  onCounterpartySearch: (query: string) => void;
  /**
   * Required, like `isCounterpartyError`: the select runs in server mode, where an
   * in-flight or failed search leaves the option list empty. Left out, the list
   * renders the affirmative "No results found" over it — the lie LR-572 is about.
   */
  isCounterpartyLoading: boolean;
  /** Search failed — the list says so instead of "no such counterparty". */
  isCounterpartyError: boolean;
  hasMoreCounterparties?: boolean;
  onLoadMoreCounterparties?: () => void;
  isLoadingMoreCounterparties?: boolean;
  /** Server-side total for the current filter, shown at the end of the row. */
  resultCount?: number;
  /**
   * Rows actually rendered. The list requests a single unpaginated page (DRF
   * PAGE_SIZE 100) and the table has no paging control, so this can be lower
   * than `resultCount` — the label must not claim rows the user cannot reach.
   */
  shownCount?: number;
  /** Current year, injected so the Year list is deterministic in tests. */
  currentYear?: number;
}

const CLEAR_COUNTERPARTY: SearchableSelectOption = { value: '', label: 'All counterparties' };

// Field shell — uppercase micro-label over the control, matching the billing
// design reference (and the label treatment already used by SavedViewDropdown /
// PartialRecallsPanel).
const Field: React.FC<{ id: string; label: string; children: React.ReactNode }> = ({
  id,
  label,
  children,
}) => (
  <div className="flex flex-col gap-[3px]">
    <label
      id={`${id}-label`}
      htmlFor={id}
      className="text-[10.5px] font-semibold uppercase tracking-[0.04em] text-gray-500"
    >
      {label}
    </label>
    {children}
  </div>
);

/**
 * Native select styled to the reference: the browser arrow is suppressed and
 * replaced with the same chevron SearchableSelect draws, so every control on the
 * row reads as one family.
 */
const FilterSelect: React.FC<
  React.SelectHTMLAttributes<HTMLSelectElement> & { width: string }
> = ({ width, className, children, ...props }) => (
  <div className={clsx('relative inline-block', width)}>
    <select
      {...props}
      className={clsx(
        'w-full appearance-none rounded-md border border-gray-300 bg-white',
        'py-1 pl-2.5 pr-8 text-sm text-gray-900',
        'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500',
        'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400',
        className,
      )}
    >
      {children}
    </select>
    <ChevronDownIcon
      className={clsx(
        'pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2',
        props.disabled ? 'text-gray-300' : 'text-gray-400',
      )}
    />
  </div>
);

/**
 * Filter panel above the billing statements table (LR-551 / T14).
 *
 * Currency is deliberately absent: after LR-553 the main statement is one
 * document per counterparty with per-currency sections, so currency is no longer
 * a property of the document. It stays relevant for the Cash Pool Interest
 * Statement (per currency per CP, LR-558) and belongs to that type's filters.
 */
export const BillingFilters: React.FC<BillingFiltersProps> = ({
  value,
  onChange,
  counterpartyOptions,
  counterpartyName,
  onCounterpartySearch,
  isCounterpartyLoading,
  isCounterpartyError,
  hasMoreCounterparties,
  onLoadMoreCounterparties,
  isLoadingMoreCounterparties,
  resultCount,
  shownCount,
  // UTC, like the period arithmetic in billing-search, so the offered years match
  // the period_start dates the backend filters on.
  currentYear = new Date().getUTCFullYear(),
}) => {
  // A shared/bookmarked link may carry a year outside the offered span (parse
  // accepts 1970-9999). Keep it in the list, or the select would render blank
  // while the table is in fact filtered by it.
  const years = React.useMemo(() => {
    const offered = billingYearOptions(currentYear);
    return value.year === undefined || offered.includes(value.year)
      ? offered
      : [...offered, value.year].sort((a, b) => b - a);
  }, [currentYear, value.year]);


  const isFiltered = hasBillingFilters(value);

  return (
    <div className="flex flex-wrap items-end gap-4">
      {/* LR-557: prev/next stepper over the same year/month the selects edit. */}
      <div className="flex flex-col gap-[3px]">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.04em] text-gray-500">
          Period
        </span>
        <BillingMonthNav value={value} onChange={onChange} />
      </div>

      <Field id="billing-filter-year" label="Year">
        <FilterSelect
          id="billing-filter-year"
          width="w-[104px]"
          value={value.year ?? ''}
          onChange={(e) => {
            const year = e.target.value ? Number(e.target.value) : undefined;
            // Clearing the year clears the month with it: a month alone has no
            // period bounds, so it would silently stop filtering.
            onChange({ ...value, year, month: year === undefined ? undefined : value.month });
          }}
        >
          <option value="">All years</option>
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </FilterSelect>
      </Field>

      <Field id="billing-filter-month" label="Month">
        <FilterSelect
          id="billing-filter-month"
          width="w-[140px]"
          // A month maps to a period range only within a known year, so it is
          // unavailable until one is picked (see parseBillingSearch).
          disabled={value.year === undefined}
          title={value.year === undefined ? 'Select a year first' : undefined}
          value={value.month ?? ''}
          onChange={(e) =>
            onChange({ ...value, month: e.target.value ? Number(e.target.value) : undefined })
          }
        >
          <option value="">All months</option>
          {BILLING_MONTHS.map((month) => (
            <option key={month.value} value={month.value}>
              {month.label}
            </option>
          ))}
        </FilterSelect>
      </Field>

      <Field id="billing-filter-cp" label="Counterparty">
        <SearchableSelect
          value={value.cp !== undefined ? String(value.cp) : ''}
          onChange={(cp) => onChange({ ...value, cp: cp ? Number(cp) : undefined })}
          options={counterpartyOptions}
          // Explicit clear entry: without it the counterparty is the one filter
          // that can only be undone by clearing all of them. Passed separately
          // rather than prepended to the options, so it survives the list's
          // loading and error states — and so an empty search can still say so.
          leadingOption={CLEAR_COUNTERPARTY}
          // The options are one searched window of the registry, so a filtered id
          // is usually not among them. Without this the trigger would read "All
          // counterparties" over a single-counterparty result set — an affirmative
          // false statement. The page resolves the real name; the id is the
          // fallback for the moment before it arrives. `||`, not `??`: an empty
          // name would fall through to the placeholder and make that same claim.
          selectedLabel={
            value.cp !== undefined
              ? counterpartyName || `Counterparty #${value.cp}`
              : undefined
          }
          placeholder="All counterparties"
          searchPlaceholder="Search counterparties…"
          className="w-[184px]"
          onSearchChange={onCounterpartySearch}
          isLoading={isCounterpartyLoading}
          isError={isCounterpartyError}
          hasMore={hasMoreCounterparties}
          onLoadMore={onLoadMoreCounterparties}
          isLoadingMore={isLoadingMoreCounterparties}
          // A button takes its accessible name from its contents, not from an
          // associated <label for> — without this the only control on the row a
          // screen reader cannot name is this one ("All counterparties, button").
          buttonProps={{
            id: 'billing-filter-cp',
            'aria-labelledby': 'billing-filter-cp-label billing-filter-cp',
            'data-testid': 'billing-filter-cp',
          }}
        />
      </Field>

      <Field id="billing-filter-type" label="Type">
        <FilterSelect
          id="billing-filter-type"
          width="w-[176px]"
          value={value.type ?? ''}
          // Narrow via the option list rather than asserting: if the options ever
          // drift from the union, that is a compile error here instead of a value
          // that reaches the URL and is silently dropped on the way back.
          onChange={(e) =>
            onChange({
              ...value,
              type: BILLING_STATEMENT_TYPES.find((t) => t.value === e.target.value)?.value,
            })
          }
        >
          <option value="">All types</option>
          {BILLING_STATEMENT_TYPES.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </FilterSelect>
      </Field>

      <Field id="billing-filter-status" label="Status">
        <FilterSelect
          id="billing-filter-status"
          width="w-[132px]"
          value={value.status ?? ''}
          onChange={(e) =>
            onChange({
              ...value,
              status: BILLING_STATUS_OPTIONS.find((s) => s.value === e.target.value)?.value,
            })
          }
        >
          <option value="">All statuses</option>
          {BILLING_STATUS_OPTIONS.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </FilterSelect>
      </Field>

      {/* Rendered only while something is filtered — an always-present, mostly
          disabled control is dead weight on the row (reference does the same). */}
      {isFiltered && (
        <button
          type="button"
          onClick={() => onChange({})}
          className="mb-px rounded-md border border-[#cfe0fb] bg-[#eef3ff] px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-[#e4ecff]"
        >
          Clear filters
        </button>
      )}

      {resultCount !== undefined && (
        <span className="ml-auto pb-[5px] text-[12.5px] tabular-nums text-gray-500">
          <span className="font-semibold text-gray-900">
            {shownCount !== undefined && shownCount < resultCount
              ? `${shownCount.toLocaleString('en-US')} of ${resultCount.toLocaleString('en-US')}`
              : resultCount.toLocaleString('en-US')}
          </span>{' '}
          {resultCount === 1 ? 'statement' : 'statements'}
        </span>
      )}
    </div>
  );
};
